import {assert} from 'Asserts';
import {findAllUpstreamMappableNodeIds} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';

import {getFieldInfoFromDataFlowEdgeHandle} from '../../flowchartIdentifierUtils';
import {
  CustomMappingData,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
} from '../../processFlowchartConfigEditorConstants';
import {
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
  TaskSchemaStaticNodeData,
} from '../../processFlowchartConfigEditorNodeTypes';
import {TransitionChange} from '../../processFlowchartConfigEditorUtils';
import {RelevantControlFlowEdge} from './transitionDataFlowEdgeSync';
import {getFieldPairKey, getNodePairKey, getTransitionId} from './transitionKeyUtils';
import {findTaskSchemaNodePair, getPTDVIdFromNode} from './transitionLookupUtils';
import {Transition} from './TransitionTypes';

/**
 * Finds all planned transitions that include the given (sourcePTDVId, targetPTDVId) pair.
 *
 * A source→target pair can be represented by multiple transitions simultaneously —
 * a single-source transition (e.g. p1 → p2) and a merging transition that includes
 * it as a subset (e.g. {p1, p3} → p2). Both should be updated together when the
 * user changes the custom mapping or transition source for the shared pair, so
 * the two transitions stay in sync (this follows mirroring logic that exists elsewhere).
 */
export function findPlannedTransitionIdsForPTDVPair(
  sourcePTDVId: string,
  targetPTDVId: string,
  transitions: Record<string, Transition>
): Array<string> {
  const ids: Array<string> = [];
  const singleSourceId = getTransitionId([sourcePTDVId], targetPTDVId);
  const singleSource = transitions[singleSourceId];
  if (singleSource != null && singleSource.isPlanned) {
    ids.push(singleSourceId);
  }
  for (const transition of Object.values(transitions)) {
    if (
      transition.id !== singleSourceId &&
      transition.isPlanned &&
      transition.targetPTDVId === targetPTDVId &&
      transition.sourcePTDVIds.includes(sourcePTDVId)
    ) {
      ids.push(transition.id);
    }
  }
  return ids;
}

/**
 * Finds all planned transitions that this PTDV pair fulfills (source->target is part of the
 * transition, and all transition sources have a path to the target)
 *
 * Similar to findNodePairsFulfillingTransition, but this transitions for the node pair
 */
export function findFulfilledPlannedTransitionIdsForNodePair({
  source,
  target,
  transitions,
  nodes,
  edges,
}: {
  source: TaskSchemaStaticNodeData;
  target: TaskSchemaStaticNodeData;
  transitions: Record<string, Transition>;
  nodes: Array<StaticFlowchartNodeData>;
  edges: Array<FlowchartStaticEdgeData>;
}): Array<string> {
  const sourcePTDVId = getPTDVIdFromNode(source);
  const targetPTDVId = getPTDVIdFromNode(target);
  const nodeMap = new Map<string, StaticFlowchartNodeData>(nodes.map((node) => [node.id, node]));
  const nodeData = nodes.map((node) => ({id: node.id, data: node}));
  const upstreamMappableNodeIds = findAllUpstreamMappableNodeIds({nodes: nodeData, edges}, target.id);
  const upstreamMappableNodes = upstreamMappableNodeIds.map((nodeId) => {
    const upstreamNode = nodeMap.get(nodeId);
    assert(isStaticTaskSchemaNodeData(upstreamNode!), 'Upstream node must be a task schema node');
    return upstreamNode;
  });
  const upstreamPTDVIds = new Set(
    upstreamMappableNodes.map((upstreamNode) => upstreamNode.taskDefinitionVersionSpec.clientId)
  );

  const fulfilledTransitionIds: Array<string> = [];
  for (const transition of Object.values(transitions)) {
    if (
      !transition.isPlanned ||
      transition.targetPTDVId !== targetPTDVId ||
      !transition.sourcePTDVIds.includes(sourcePTDVId)
    ) {
      continue;
    }

    const allSourcesFulfilled = transition.sourcePTDVIds.every((srcPTDV) => upstreamPTDVIds.has(srcPTDV));

    if (allSourcesFulfilled) {
      fulfilledTransitionIds.push(transition.id);
    }
  }

  return fulfilledTransitionIds;
}

/**
 * Finds every task node pair that fulfills a transition: pairs `(sourceNode, targetNode)`
 * connected by a control flow path (possibly through a router) where `sourceNode`'s PTDV is one
 * of the transition's sources and `targetNode`'s PTDV is the transition's target.
 *
 * For merging transitions, a pair only counts when the entire merge is fulfilled at that target
 * node (all source ptdvs have control flow edges to that target node).
 *
 * Similar to findFulfilledPlannedTransitionIdsForNodePair, but this finds node pairs for the
 * transition
 */
export function findNodePairsFulfillingTransition({
  nodes,
  edges,
  transition,
}: {
  nodes: Array<StaticFlowchartNodeData>;
  edges: Array<FlowchartStaticEdgeData>;
  transition: Pick<Transition, 'sourcePTDVIds' | 'targetPTDVId'>;
}): Array<RelevantControlFlowEdge> {
  const transitionSourcePTDVIds = new Set(transition.sourcePTDVIds);
  // keyed by `source id:target id` for deduping
  const relevantControlFlowEdges: Record<string, RelevantControlFlowEdge> = {};
  const nodeData = nodes.map((node) => ({id: node.id, data: node}));
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));

  for (const node of nodes) {
    if (!isStaticTaskSchemaNodeData(node)) {
      continue;
    }
    const ptdvId = node.taskDefinitionVersionSpec.clientId;
    if (transition.targetPTDVId !== ptdvId) {
      continue;
    }
    const upstreamMappableNodeIds = findAllUpstreamMappableNodeIds({nodes: nodeData, edges}, node.id);
    const upstreamMappableNodes = upstreamMappableNodeIds.map((nodeId) => {
      const upstreamNode = nodeMap.get(nodeId);
      assert(isStaticTaskSchemaNodeData(upstreamNode!), 'Upstream node must be a task schema node');
      return upstreamNode;
    });
    const upstreamPTDVIds = new Set(
      upstreamMappableNodes.map((upstreamNode) => upstreamNode.taskDefinitionVersionSpec.clientId)
    );
    if (!transition.sourcePTDVIds.every((srcPTDV) => upstreamPTDVIds.has(srcPTDV))) {
      continue;
    }

    for (const upstreamNode of upstreamMappableNodes) {
      const upstreamNodeId = upstreamNode.id;
      const upstreamPTDVId = upstreamNode.taskDefinitionVersionSpec.clientId;
      if (!transitionSourcePTDVIds.has(upstreamPTDVId)) {
        continue;
      }
      const ptdvPairKey = `${upstreamPTDVId}::${ptdvId}`;
      relevantControlFlowEdges[getNodePairKey(upstreamNodeId, node.id)] = {
        sourceNodeId: upstreamNodeId,
        targetNodeId: node.id,
        ptdvPairKey,
      };
    }
  }

  return Object.values(relevantControlFlowEdges);
}

export interface CreateTransitionChangeForCustomMappingParams {
  edgeId: string;
  /** Pass null to create a change that removes the custom mapping for this edge's field pair. */
  customMappingData: CustomMappingData | null;
  edges: Array<FlowchartStaticEdgeData>;
  nodes: Array<StaticFlowchartNodeData>;
  transitions: Record<string, Transition>;
}

export function createTransitionChangesForCustomMapping({
  edgeId,
  customMappingData,
  edges,
  nodes,
  transitions,
}: CreateTransitionChangeForCustomMappingParams): Array<TransitionChange> {
  const edge = edges.find((e) => e.id === edgeId);
  if (!edge || edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
    return [];
  }

  const nodePair = findTaskSchemaNodePair(edge, nodes);
  if (!nodePair) {
    return [];
  }

  const sourcePTDVId = getPTDVIdFromNode(nodePair.sourceNode);
  const targetPTDVId = getPTDVIdFromNode(nodePair.targetNode);
  const transitionIds = findPlannedTransitionIdsForPTDVPair(sourcePTDVId, targetPTDVId, transitions);
  if (transitionIds.length === 0) {
    return [];
  }

  const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  const targetHandle = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

  const fieldPairKey = getFieldPairKey({
    sourcePTDVId,
    sourceSectionType: sourceHandle.sectionType,
    sourceFieldClientId: sourceHandle.fieldClientId,
    targetPTDVId,
    targetSectionType: targetHandle.sectionType,
    targetFieldClientId: targetHandle.fieldClientId,
  });

  return transitionIds.map((transitionId) => ({
    type: 'update',
    id: transitionId,
    changes: {
      customMapping: {fieldPairKey, value: customMappingData},
    },
  }));
}
